/**
 * Chat tokens — derived from the central design system.
 *
 * Preserves the existing shape so all chat consumers (ChatPanel,
 * CopilotPage, WorkspacePanel, etc.) keep working without edits.
 */

import { TenantBranding } from '../hooks/useBranding';
import { buildTokens, type Mode } from './designSystem';
import chroma from 'chroma-js';

export interface ChatToneTokens {
  panelBg: string;
  panelBorder: string;
  userBg: string;
  userBorder: string;
  assistantBg: string;
  assistantBorder: string;
  codeBg: string;
  chipBg: string;
  chipHoverBg: string;
  chipBorder: string;
  inputBg: string;
  inputBorder: string;
  inputFocusBorder: string;
  border: string;
  messageShadow: string;
  inputShadow: string;
  roleLabelColor: string;
  messageHeaderBg: string;
}

const buildTone = (mode: Mode, branding?: TenantBranding | null): ChatToneTokens => {
  const t = buildTokens(mode, branding);
  const isLight = mode === 'light';
  return {
    panelBg: t.chat.panelBg,
    panelBorder: t.chat.panelBorder,
    userBg: t.chat.userBubbleBg,
    userBorder: chroma(t.brand.primary).alpha(isLight ? 0.18 : 0.30).css(),
    assistantBg: t.chat.assistantBubbleBg,
    assistantBorder: t.border.default,
    codeBg: t.chat.codeBg,
    chipBg: isLight ? t.surface.sunken : t.chat.codeBg,
    chipHoverBg: t.chat.chipHoverBg,
    chipBorder: t.chat.chipBorder,
    inputBg: t.chat.inputBg,
    inputBorder: t.chat.inputBorder,
    inputFocusBorder: t.chat.inputFocusBorder,
    border: t.border.subtle,
    messageShadow: t.chat.messageShadow,
    inputShadow: t.chat.inputShadow,
    roleLabelColor: t.text.secondary,
    messageHeaderBg: isLight ? 'rgba(15,18,24,0.02)' : 'rgba(255,255,255,0.03)',
  };
};

// Tenant-aware accessor (preferred for all new code).
export function getChatTokens(branding?: TenantBranding | null) {
  return {
    light: buildTone('light', branding),
    dark: buildTone('dark', branding),
  };
}

// Backwards-compatible default export — used widely in chat code.
export const chatTokens = {
  light: buildTone('light', null),
  dark: buildTone('dark', null),
};
